import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "../../lib/api";
import { usePolling } from "../../lib/usePolling";
import { pfield } from "../../lib/labels";
import { EmailVerifyNotice } from "../../components/EmailVerifyNotice";
import { ClipboardList, BookOpen, FileSignature, PlusCircle } from "lucide-react";

const STATUS_LABEL = {
  profile_incomplete: "Profilo incompleto",
  candidatura_inviata: "Candidatura inviata — in valutazione",
  integrazioni_richieste: "Integrazioni richieste",
  candidatura_approvata: "Approvata — in attesa dell'accordo",
  candidatura_rifiutata: "Candidatura rifiutata",
  pronto_attivazione: "Accordo perfezionato — in attesa di attivazione",
  attivo: "Attivo",
  sospeso: "Sospeso",
  cessato: "Cessato",
};

export default function AgentDashboard() {
  const [me, setMe] = useState(null);
  const [badges, setBadges] = useState({});

  const loadMe = async () => { try { const r = await http.get("/crm/me/agent"); setMe(r.data); } catch { /* noop */ } };
  const loadBadges = async () => { try { const r = await http.get("/crm/me/badges"); setBadges(r.data || {}); } catch { /* noop */ } };

  useEffect(() => { loadMe(); loadBadges(); }, []);
  usePolling(loadBadges, { interval: 10000, enabled: true });

  if (!me) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;
  const name = me.profile?.first_name ? `${me.profile.first_name} ${me.profile.last_name || ""}` : me.email;
  const active = me.status === "attivo";

  return (
    <div data-testid="agent-dashboard" className="max-w-4xl mx-auto">
      <h1 className="font-serif text-3xl mb-1">Benvenuto, {name}</h1>
      <p className="text-sm text-neutral-500 mb-6">
        Siamo felici che tu voglia accompagnare i saloni verso un nuovo modo di lavorare.
      </p>

      <div className="card-clean p-5 mb-5" data-testid="agent-status-card">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Stato candidatura</div>
        <div className="text-xl font-semibold" data-testid="agent-status-value" style={{ color: "var(--brand-dark)" }}>
          {STATUS_LABEL[me.status] || me.status}
        </div>
        {!me.completeness?.complete && (
          <div className="mt-3 text-sm">
            <span className="text-rose-700">Campi mancanti: </span>
            {me.completeness.missing.map(pfield).join(", ")}
            <div className="mt-2"><Link to="/agent/profile" className="underline text-sm" data-testid="agent-dash-complete">Completa il tuo profilo</Link></div>
          </div>
        )}
        {!me.email_verified && <EmailVerifyNotice email={me.email} />}
      </div>

      {active && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="agent-dash-indicators">
          <IndicatorCard title="Attività da svolgere" count={badges.attivita} to="/agent/deals" testid="dash-ind-attivita" icon={ClipboardList} />
          <IndicatorCard title="Materiali da leggere" count={badges.materiali} to="/agent/materials?filter=pending" testid="dash-ind-materiali" icon={BookOpen} />
          <IndicatorCard title="Documenti da firmare" count={badges.documenti} to="/agent/documenti?filter=pending" testid="dash-ind-documenti" icon={FileSignature} />
          <IndicatorCard title="Preventivi extra" count={badges.extra} to="/agent/extra?filter=preventivo" testid="dash-ind-extra" icon={PlusCircle} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Tile title="Profilo" to="/agent/profile" desc="Dati, foto e territorialità" testid="tile-profile" />
        <Tile title="Accordo" to="/agent/agreement" desc="Scarica e carica la copia firmata" testid="tile-agreement" />
        <Tile title="Provvigioni" to="/agent/commissions" desc="Prospetti mensili" disabled={!active} testid="tile-commissions" />
      </div>

      {!active && (
        <p className="text-xs text-neutral-500 mt-6" data-testid="agent-not-active-note">
          Le funzioni commerciali (contatti, provvigioni) saranno disponibili dopo l'attivazione manuale del tuo account.
        </p>
      )}
    </div>
  );
}

function IndicatorCard({ title, count, to, testid, icon: Icon }) {
  const n = count || 0;
  return (
    <Link to={to} data-testid={testid} className="card-clean p-4 hover:shadow-md transition-shadow block">
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5" style={{ color: "var(--brand-dark)" }} />
        {n > 0 && <span data-testid={`${testid}-badge`} className="text-[11px] px-2 py-0.5 rounded-full bg-rose-600 text-white font-semibold">{n}</span>}
      </div>
      <div className="text-2xl font-semibold" style={{ color: "var(--brand-dark)" }} data-testid={`${testid}-count`}>{n}</div>
      <div className="text-xs text-neutral-500 mt-0.5">{title}</div>
    </Link>
  );
}

function Tile({ title, desc, to, disabled, testid }) {
  const inner = (
    <div className={`card-clean p-5 h-full ${disabled ? "opacity-50" : "hover:shadow-md"}`} data-testid={testid}>
      <div className="font-serif text-lg mb-1" style={{ color: "var(--brand-dark)" }}>{title}</div>
      <div className="text-xs text-neutral-500">{desc}</div>
    </div>
  );
  return disabled ? inner : <Link to={to}>{inner}</Link>;
}
